import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Printer, Mail, Link as LinkIcon, QrCode, Share2, ChevronRight, ArrowLeft, ShieldAlert } from 'lucide-react';
export function CrisisResponsePage() {
  const [checklist, setChecklist] = useState([false, false, false, false, false]);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [progress, setProgress] = useState(0);
  const [scenarioAnswer, setScenarioAnswer] = useState<string | null>(null);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleCheck = (index: number) => {
    const newChecklist = [...checklist];
    newChecklist[index] = !newChecklist[index];
    setChecklist(newChecklist);
    setHasInteracted(true);
    // Update progress based on checklist and scenario (up to 50% before completion)
    calculateProgress(newChecklist, scenarioAnswer);
  };
  const handleScenario = (answer: string) => {
    setScenarioAnswer(answer);
    setHasInteracted(true);
    calculateProgress(checklist, answer);
  };
  const calculateProgress = (currentChecklist: boolean[], currentScenario: string | null) => {
    if (isCompleted) return;
    let score = 0;
    const totalItems = currentChecklist.length + 1; // checklist items + scenario
    const completedChecks = currentChecklist.filter(Boolean).length;
    score += completedChecks;
    if (currentScenario !== null) {
      score += 1;
    }
    setProgress(Math.round(score / totalItems * 50));
  };
  const handleComplete = () => {
    setIsCompleted(true);
    setProgress(100);
    // Small delay to allow render before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };
  return <div className="min-h-screen w-full bg-white text-[#1a0647]" data-id="element-1031">
      {/* Mandatory Typography Imports & Classes */}
      <style data-id="element-1032">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@400;500;600;700;800&display=swap');
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
      `}</style>

      {/* Simple Navigation Bar */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm" data-id="element-1033">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-1034">
          <Link to="/resources" className="text-gray-600 hover:text-[#8528e2] font-manrope font-semibold text-base transition-colors flex items-center gap-2 min-h-[44px] min-w-[44px]" data-id="element-1035">
            <ArrowLeft size={20} aria-hidden="true" data-id="element-1036" />
            Back to Resources
          </Link>
          <img src="https://cdn.magicpatterns.com/uploads/8EVxVy8dkAVEdmFcPkswyr/Caveo_2026_Logo.png" alt="Caveo Logo" className="h-6 w-auto" data-id="element-1037" />
        </div>
      </header>

      <main data-id="element-1038">
        {/* 1. Module Header (Hero Block) */}
        <section className="bg-[#1a0647] text-white py-12 md:py-16 px-4 md:px-8" data-id="element-1039">
          <div className="max-w-3xl mx-auto" data-id="element-1040">
            <h1 className="font-manrope text-3xl md:text-5xl font-bold mb-4 text-white leading-tight" data-id="element-1041">
              Crisis Response Guide
            </h1>
            <p className="font-inter text-lg md:text-xl mb-8 text-gray-200 leading-relaxed" data-id="element-1042">
              After this module, you'll know exactly what to do when a digital
              crisis hits—whether it's a hacked account, a scam, or identity
              theft.
            </p>

            <div className="inline-flex items-center gap-2 bg-[#01ced1] text-[#1a0647] px-4 py-2 rounded-full font-manrope font-bold text-base mb-10 min-h-[44px]" data-id="element-1043">
              <span aria-hidden="true" data-id="element-1044">⏱️</span> 15 minutes to complete
            </div>

            <div className="space-y-3 bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-1045">
              <div className="flex justify-between font-manrope font-semibold text-base" data-id="element-1046">
                <span id="progress-label" data-id="element-1047">Your progress</span>
                <span data-id="element-1048">{progress}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-labelledby="progress-label" data-id="element-1049">
                <div className="bg-[#214ae9] h-full transition-all duration-700 ease-out" style={{
                width: `${progress}%`
              }} data-id="element-1050" />
              </div>
            </div>
          </div>
        </section>

        {/* 2. The Hook (Story Block) */}
        <section className="bg-[#eae6dc] py-12 md:py-16 px-4 md:px-8" data-id="element-1051">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-6 items-start" data-id="element-1052">
            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-3xl border border-gray-200" aria-hidden="true" data-id="element-1053">
              🚨
            </div>
            <div className="flex-1 space-y-4" data-id="element-1054">
              <p className="font-inter text-gray-700 text-lg leading-relaxed" data-id="element-1055">
                Robert was making dinner when his phone buzzed with an urgent
                text from his "bank" about a fraudulent $800 charge. Panicking,
                he clicked the link and entered his login details to stop the
                transfer. A minute later, he realized the website URL looked
                slightly wrong. His heart sank. He had just handed his actual
                bank password to a scammer.
              </p>
              <p className="font-inter text-[#1a0647] text-xl font-bold leading-relaxed" data-id="element-1056">
                In this module, you'll learn the exact steps to take to minimize
                damage, secure your accounts, and recover your identity when the
                worst happens.
              </p>
            </div>
          </div>
        </section>

        {/* 3. Concept Cards (One Idea Per Card) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-1057">
          <div className="max-w-4xl mx-auto" data-id="element-1058">
            <h2 className="sr-only" data-id="element-1059">Key Concepts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1060">
              {/* Card 1 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1061">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-1062">
                  Hacked Accounts
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-1063">
                  If you're locked out of an account or see activity you didn't
                  authorize, immediately use the "Forgot Password" tool to
                  regain access. Once in, change your password, turn on
                  two-factor authentication (2FA), and check your recovery email
                  and phone number to ensure the hacker didn't change them.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-1064">
                  Think of this like changing the locks on your house
                  immediately after losing your keys.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1065">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-1066">
                  Financial Scams
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-1067">
                  If you see unauthorized charges or realize you gave your
                  financial info to a scammer, call your bank or credit card
                  company immediately using the number on the back of your card.
                  They can freeze your account, issue a new card, and start the
                  dispute process to get your money back.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-1068">
                  Think of this like hitting the emergency stop button on a
                  runaway train.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1069">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-1070">
                  Identity Theft
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-1071">
                  If your Social Security number or sensitive data is
                  compromised, immediately place a fraud alert or credit freeze
                  with the three major credit bureaus (Equifax, Experian,
                  TransUnion). Then, file a report at IdentityTheft.gov to get a
                  personalized recovery plan.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-1072">
                  Think of a credit freeze like putting your financial
                  reputation in a vault that only you can open.
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1073">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-1074">
                  Tech Support Scams
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-1075">
                  If you get a terrifying pop-up saying your computer has a
                  virus and you must call a number, do not call it. Real tech
                  companies do not operate this way. Force quit your browser or
                  restart your computer, then run a scan with your actual
                  antivirus software.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-1076">
                  Think of this like a stranger banging on your window shouting
                  that your house is on fire, while offering to sell you a
                  bucket of water.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Practice Activity (Interactive Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white border-t border-gray-100" data-id="element-1077">
          <div className="max-w-3xl mx-auto" data-id="element-1078">
            <h2 className="font-manrope text-[#1a0647] text-3xl md:text-4xl font-bold mb-6" data-id="element-1079">
              Try It Now
            </h2>
            <p className="font-inter text-gray-700 text-lg mb-10 leading-relaxed" data-id="element-1080">
              The best time to prepare for a crisis is before it happens. Tap
              each step as you complete it to build your emergency response kit.
            </p>

            <div className="space-y-4 mb-12" role="group" aria-label="Preparation Checklist" data-id="element-1081">
              {['Save the customer service numbers for your bank and credit cards in your phone contacts.', 'Bookmark IdentityTheft.gov in your web browser.', 'Verify that the recovery email and phone number on your primary email account are up to date.', 'Write down the phone numbers for the three credit bureaus (Equifax: 800-685-1111, Experian: 888-397-3742, TransUnion: 888-909-8872) and keep them with your important documents.', 'Check your credit report for free at AnnualCreditReport.com to ensure there are no existing unauthorized accounts.'].map((item, i) => <button key={i} onClick={() => handleCheck(i)} className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 flex items-start gap-5 min-h-[44px] ${checklist[i] ? 'border-[#01ced1] bg-[#01ced1]/5 shadow-sm' : 'border-gray-200 bg-[#f8f8f8] hover:border-[#8528e2]/40 hover:bg-white'}`} aria-pressed={checklist[i]} data-id="element-1082">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 mt-0.5 transition-colors ${checklist[i] ? 'bg-[#01ced1] border-[#01ced1] text-white' : 'border-gray-300 text-transparent bg-white'}`} data-id="element-1083">
                    <CheckCircle2 size={20} aria-hidden="true" data-id="element-1084" />
                  </div>
                  <span className={`font-inter text-lg leading-relaxed ${checklist[i] ? 'text-[#1a0647] font-semibold' : 'text-gray-700'}`} data-id="element-1085">
                    {item}
                  </span>
                </button>)}
            </div>

            {/* Scenario Choice */}
            <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200" data-id="element-1086">
              <div className="flex items-center gap-3 mb-6" data-id="element-1087">
                <ShieldAlert size={28} className="text-[#8528e2]" aria-hidden="true" data-id="element-1088" />
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold" data-id="element-1089">
                  Quick Check
                </h3>
              </div>
              <p className="font-inter text-gray-700 text-lg mb-6 leading-relaxed" data-id="element-1090">
                You notice a $500 charge on your credit card statement that you
                definitely didn't make. What is your very first step?
              </p>

              <div className="space-y-3" data-id="element-1091">
                <button onClick={() => handleScenario('A')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'A' ? 'border-[#214ae9] bg-[#214ae9]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-1092">
                  A: Wait a few days to see if the charge disappears or corrects
                  itself.
                </button>
                <button onClick={() => handleScenario('B')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'B' ? 'border-[#01ced1] bg-[#01ced1]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-1093">
                  B: Call the number on the back of your credit card immediately
                  to report the fraud and freeze the card.
                </button>
                <button onClick={() => handleScenario('C')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'C' ? 'border-[#214ae9] bg-[#214ae9]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-1094">
                  C: Try to look up the merchant online and call them to ask for
                  a refund.
                </button>
              </div>

              {scenarioAnswer === 'B' && <div className="mt-6 p-4 bg-[#01ced1]/10 border-l-4 border-[#01ced1] rounded-r-lg animate-in fade-in" data-id="element-1095">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-1096">
                    That's exactly right! Your credit card company is your best
                    advocate here. They can instantly freeze the card to prevent
                    further charges, issue you a new card, and start the dispute
                    process to remove the fraudulent charge.
                  </p>
                </div>}
              {scenarioAnswer === 'A' && <div className="mt-6 p-4 bg-[#214ae9]/10 border-l-4 border-[#214ae9] rounded-r-lg animate-in fade-in" data-id="element-1097">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-1098">
                    Not quite — time is critical when dealing with fraud. If
                    someone has your card information, they will likely make
                    more charges. Call the number on the back of your card
                    immediately to freeze it and dispute the charge.
                  </p>
                </div>}
              {scenarioAnswer === 'C' && <div className="mt-6 p-4 bg-[#214ae9]/10 border-l-4 border-[#214ae9] rounded-r-lg animate-in fade-in" data-id="element-1099">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-1100">
                    Actually, contacting the merchant is usually a dead end, and
                    if it's a scammer, you're just giving them more of your
                    time. Your credit card company has the power to reverse the
                    charge and issue you a new, secure card. Call the number on
                    the back of your card immediately.
                  </p>
                </div>}
            </div>
          </div>
        </section>

        {/* 5. Honesty Gate (Pre-Commitment Block) */}
        <section className="py-8 px-4 md:px-8 bg-white" data-id="element-1101">
          <div className="max-w-3xl mx-auto" data-id="element-1102">
            <div className="border-l-4 border-[#8528e2] bg-[#8528e2]/5 p-6 md:p-8 rounded-r-xl mb-8" data-id="element-1103">
              <p className="font-inter text-[#1a0647] text-lg leading-relaxed font-medium" data-id="element-1104">
                Before you confirm: this step matters for your family's
                protection. Please only mark it complete if you actually did it.
                It's completely okay to come back to this later.
              </p>
            </div>

            {hasInteracted && !isCompleted && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1105">
                <button onClick={handleComplete} className="bg-[#8528e2] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#b063ff] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center w-full md:w-auto shadow-md" data-id="element-1106">
                  I completed this step
                </button>
              </div>}
          </div>
        </section>

        {/* 6. Reflection Prompt (Personal Connection Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-1107">
          <div className="max-w-3xl mx-auto" data-id="element-1108">
            <div className="bg-[#f8f8f8] border border-gray-200 p-8 md:p-10 rounded-2xl shadow-sm" data-id="element-1109">
              <div className="inline-block bg-[#eae6dc] text-[#1a0647] font-manrope font-bold px-4 py-1.5 rounded-md text-sm mb-6 uppercase tracking-wider" data-id="element-1110">
                Reflect for a Moment
              </div>
              <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold mb-8 leading-tight" data-id="element-1111">
                Which of these crisis scenarios worries you the most, and what's
                one step you can take today to be better prepared?
              </h3>
              <textarea value={reflectionText} onChange={e => setReflectionText(e.target.value)} placeholder="Take a moment to think about this..." className="w-full bg-white border border-gray-300 rounded-xl p-5 font-inter text-gray-700 text-lg min-h-[160px] focus:outline-none focus:ring-2 focus:ring-[#8528e2] focus:border-transparent resize-y shadow-inner" aria-label="Reflection input" data-id="element-1112" />
              <p className="font-inter text-gray-500 text-sm mt-4" data-id="element-1113">
                This is just for you — your response isn't stored or shared.
              </p>
            </div>
          </div>
        </section>

        {/* 7. Takeaway Block (Action Anchor) */}
        <section className="bg-[#1a0647] text-white py-20 px-4 md:px-8" data-id="element-1114">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1115">
            <p className="font-inter text-2xl md:text-3xl font-bold leading-relaxed mb-12" data-id="element-1116">
              The single most important action you can take today is to save the
              phone numbers for your bank and credit card companies in your
              phone contacts.
            </p>

            <div className="flex flex-wrap justify-center gap-4 md:gap-6" data-id="element-1117">
              <button onClick={() => window.print()} className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-1118">
                <Printer size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-1119" />
                <span className="font-manrope text-sm font-semibold" data-id="element-1120">
                  Print
                </span>
              </button>
              <a href="mailto:?subject=Helpful guide on digital crisis response&body=I found this guide on handling digital emergencies really helpful. You should check it out." className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5 no-underline text-white" data-id="element-1121">
                <Mail size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-1122" />
                <span className="font-manrope text-sm font-semibold" data-id="element-1123">
                  Email to myself
                </span>
              </a>
              <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-1124">
                <LinkIcon size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-1125" />
                <span className="font-manrope text-sm font-semibold" data-id="element-1126">
                  Copy link
                </span>
              </button>
              <button title="Coming soon" className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5 cursor-not-allowed opacity-70" data-id="element-1127">
                <QrCode size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-1128" />
                <span className="font-manrope text-sm font-semibold" data-id="element-1129">
                  QR code
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* 8. Completion Confirmation */}
        {isCompleted && <section className="bg-[#01ced1] py-16 md:py-24 px-4 md:px-8 animate-in fade-in slide-in-from-bottom-8 duration-1000" data-id="element-1130">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1131">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" data-id="element-1132">
                <CheckCircle2 size={40} className="text-[#01ced1]" aria-hidden="true" data-id="element-1133" />
              </div>
              <h2 className="font-manrope text-[#1a0647] text-3xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-1134">
                Nice work. The Crisis Response module is complete. You're better
                prepared to handle digital emergencies.
              </h2>

              <div className="bg-white/30 p-8 rounded-2xl mt-10 max-w-lg mx-auto backdrop-blur-sm border border-white/40 shadow-sm" data-id="element-1135">
                <p className="font-inter text-[#1a0647] text-xl font-bold mb-6" data-id="element-1136">
                  Share with your family
                </p>
                <a href="mailto:?subject=Helpful guide on digital crisis response&body=I found this guide on handling digital emergencies really helpful. You should check it out." className="bg-[#1a0647] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#2a0a6b] transition-colors min-h-[44px] inline-flex items-center justify-center gap-3 shadow-md w-full sm:w-auto no-underline" data-id="element-1137">
                  <Share2 size={20} aria-hidden="true" data-id="element-1138" />
                  Send to a family member
                </a>
              </div>

              <div className="mt-16 text-left max-w-2xl mx-auto" data-id="element-1139">
                <Link to="/resources" className="block bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all group no-underline border-2 border-transparent hover:border-[#8528e2]/50" data-id="element-1140">
                  <p className="font-manrope text-[#8528e2] font-bold text-sm uppercase tracking-wider mb-3" data-id="element-1141">
                    What's Next
                  </p>
                  <div className="flex items-center justify-between" data-id="element-1142">
                    <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold" data-id="element-1143">
                      Back to Resources
                    </h3>
                    <div className="w-12 h-12 rounded-full bg-[#f8f8f8] flex items-center justify-center group-hover:bg-[#8528e2] group-hover:text-white transition-colors text-[#1a0647] shadow-sm" data-id="element-1144">
                      <ChevronRight size={24} aria-hidden="true" data-id="element-1145" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </section>}
      </main>
    </div>;
}